import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PortalNav } from "@/components/PortalNav";
import { useAuth } from "@/context/AuthContext";
import {
  Wrench, Plus, Pencil, Trash2, Loader2, ClipboardList, CalendarClock, Users, X,
  GripVertical, Search, User, MapPin, Download, Mail, Camera, ImagePlus, Calendar, Repeat,
} from "lucide-react";

const WO_STATUSES = ["Scheduled", "Dispatched", "In Progress", "Completed", "Cancelled"];
const WO_PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const SERVICE_TABS = ["dispatch", "schedule", "orders", "techs", "coverage"];
const statusTop = (s) =>
  s === "Completed" ? "border-t-[#10B981]" : s === "Cancelled" ? "border-t-[#EF4444]" : s === "In Progress" ? "border-t-[#2563EB]" : s === "Dispatched" ? "border-t-[#D4AF37]" : "border-t-zinc-300";
const priorityBadge = (p) =>
  p === "Urgent" ? "bg-[#EF4444]/10 text-[#EF4444]" : p === "High" ? "bg-[#D97706]/10 text-[#D97706]" : p === "Low" ? "bg-zinc-100 text-zinc-500" : "bg-[#2563EB]/10 text-[#2563EB]";

export default function ServicePortal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(SERVICE_TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "dispatch");
  const [workOrders, setWorkOrders] = useState([]);
  const [techs, setTechs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prefill, setPrefill] = useState(null);

  const loadAll = async () => {
    try {
      const [wRes, tRes, cRes] = await Promise.all([
        api.get("/service/work-orders"),
        api.get("/service/technicians"),
        api.get("/crm/customers"),
      ]);
      setWorkOrders(wRes.data.work_orders || []);
      setTechs(tRes.data.technicians || []);
      setCustomers(cRes.data.customers || []);
    } catch (e) { toast.error("Could not load service data"); }
    try { const iRes = await api.get("/sales/inventory"); setInventory(iRes.data.items || []); } catch (e) { /* sales reps have no inventory access — expected 403 */ console.debug("inventory unavailable for this role", e?.response?.status); }
    setLoading(false);
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (SERVICE_TABS.includes(t) && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const changeTab = (key) => { setTab(key); setSearchParams(key === "dispatch" ? {} : { tab: key }, { replace: true }); };

  useEffect(() => {
    if (searchParams.get("new_wo") === "1") {
      setTab("orders");
      setPrefill({ customer_id: searchParams.get("customer") || "", title: searchParams.get("title") || "" });
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Wrench className="h-7 w-7 text-[#D4AF37]" />
          <h1 className="font-display font-extrabold text-3xl tracking-tight text-[#0F172A]">Service Dispatch</h1>
        </div>
        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>
        ) : (
          <>
            {tab === "dispatch" && <DispatchBoard workOrders={workOrders} customers={customers} techs={techs} inventory={inventory} onReload={loadAll} />}
            {tab === "schedule" && <ScheduleTab workOrders={workOrders} customers={customers} techs={techs} inventory={inventory} onReload={loadAll} />}
            {tab === "orders" && <WorkOrdersTab workOrders={workOrders} customers={customers} techs={techs} inventory={inventory} onReload={loadAll} prefill={prefill} onPrefillConsumed={() => setPrefill(null)} />}
            {tab === "techs" && <TechniciansTab techs={techs} onReload={loadAll} />}
            {tab === "coverage" && <PmCoverageTab />}
          </>
        )}
      </main>
    </div>
  );
}

/* ---------------- PM Coverage ---------------- */
function CoverageList({ title, rows, testid, empty }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-white border border-zinc-200" data-testid={testid}>
      <div className="px-5 py-3 border-b border-zinc-200 font-display font-bold text-[#0F172A]">{title}</div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-zinc-400 text-sm">{empty}</div>
      ) : (
        <div className="divide-y divide-zinc-100 max-h-[360px] overflow-auto">
          {rows.map((r) => (
            <div key={r.name} className="px-5 py-2.5 flex items-center gap-3">
              <span className="flex-1 text-sm text-[#0F172A] truncate">{r.name}</span>
              <div className="w-28 h-2 bg-zinc-100 rounded-full overflow-hidden"><div className="h-full bg-[#D4AF37]" style={{ width: `${(r.count / max) * 100}%` }} /></div>
              <span className="text-sm font-semibold text-zinc-700 w-6 text-right">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PmCoverageTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/service/pm-coverage")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load PM coverage"))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>;
  if (err) return <div className="p-4 bg-white border border-zinc-200 text-sm text-red-700" data-testid="pm-coverage-error">{err}</div>;
  const stats = [
    { label: "Active PMs", value: data.active_total, cls: "text-[#0F172A]" },
    { label: "Overdue", value: data.overdue, cls: "text-[#EF4444]" },
    { label: "Due \u2264 30d", value: data.due_30, cls: "text-[#D4AF37]" },
    { label: "Due \u2264 60d", value: data.due_60, cls: "text-[#B8860B]" },
    { label: "Due \u2264 90d", value: data.due_90, cls: "text-zinc-700" },
  ];
  return (
    <div className="space-y-6" data-testid="service-pm-coverage">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-zinc-200 p-4" data-testid={`pm-stat-${s.value}`}>
            <div className={`text-3xl font-display font-extrabold ${s.cls}`}>{s.value}</div>
            <div className="text-xs text-zinc-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CoverageList title="By Customer" rows={data.by_customer} testid="pm-by-customer" empty="No active recurring PMs." />
        <CoverageList title="By Technician" rows={data.by_technician} testid="pm-by-tech" empty="No active recurring PMs." />
      </div>
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-3 border-b border-zinc-200 font-display font-bold text-[#0F172A] flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#D4AF37]" /> Upcoming PMs (next 90 days)</div>
        {data.upcoming.length === 0 ? (
          <div className="p-8 text-center text-zinc-400 text-sm">Nothing due in the next 90 days.</div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm" data-testid="pm-upcoming-table">
              <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wide">
                <tr><th className="text-left px-4 py-2">WO #</th><th className="text-left px-4 py-2">Title</th><th className="text-left px-4 py-2">Customer</th><th className="text-left px-4 py-2">Technician</th><th className="text-left px-4 py-2">Due</th><th className="text-left px-4 py-2">In</th></tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.upcoming.map((u) => (
                  <tr key={u.id} data-testid={`pm-upcoming-${u.id}`} className={u.days < 0 ? "bg-red-50" : ""}>
                    <td className="px-4 py-2 font-mono text-xs text-zinc-500">{u.work_order_number}</td>
                    <td className="px-4 py-2 text-[#0F172A]">{u.title || "\u2014"}</td>
                    <td className="px-4 py-2 text-zinc-600">{u.customer_name}</td>
                    <td className="px-4 py-2 text-zinc-600">{u.technician_name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#B8860B]">{(u.scheduled_date || "").slice(0, 10)}</td>
                    <td className="px-4 py-2 text-xs">{u.days < 0 ? `${-u.days}d overdue` : `${u.days}d`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Dispatch Board ---------------- */
function DispatchBoard({ workOrders, customers, techs, inventory, onReload }) {
  const [items, setItems] = useState(workOrders);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [editing, setEditing] = useState(null);
  const [emailing, setEmailing] = useState(null);
  useEffect(() => { setItems(workOrders); }, [workOrders]);

  const move = async (id, status) => {
    const prev = items;
    setItems((xs) => xs.map((w) => (w.id === id ? { ...w, status } : w)));
    try { await api.patch(`/service/work-orders/${id}/status`, { status }); toast.success(`Moved to ${status}`); }
    catch (err) { setItems(prev); toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not move"); }
  };
  const drop = (status) => { if (drag) move(drag, status); setDrag(null); setOver(null); };
  const inCol = (s) => items.filter((w) => (w.status || "Scheduled") === s);

  return (
    <>
      <div className="flex items-center justify-end mb-4">
        <button onClick={() => setEditing("new")} data-testid="service-dispatch-new-wo" className="flex items-center gap-2 bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors">
          <Plus className="h-4 w-4" /> New Work Order
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="service-dispatch-board">
        {WO_STATUSES.map((s) => (
          <div key={s} onDragOver={(e) => { e.preventDefault(); setOver(s); }} onDrop={() => drop(s)}
            data-testid={`service-col-${s}`}
            className={`bg-zinc-50 border border-zinc-200 border-t-2 ${statusTop(s)} p-2 min-h-[160px] ${over === s ? "ring-2 ring-[#D4AF37]" : ""}`}>
            <div className="flex items-center justify-between px-1 pb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-600">{s}</span>
              <span className="text-[10px] text-zinc-400">{inCol(s).length}</span>
            </div>
            <div className="space-y-2">
              {inCol(s).map((w) => (
                <div key={w.id} draggable onDragStart={() => setDrag(w.id)} onDragEnd={() => { setDrag(null); setOver(null); }}
                  onClick={() => setEditing(w)} data-testid={`service-card-${w.id}`}
                  className="border border-zinc-200 bg-white p-2.5 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow">
                  <div className="flex items-start gap-1.5">
                    <GripVertical className="h-4 w-4 text-zinc-300 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className="flex items-center gap-1 min-w-0">
                          <span className="font-mono text-[11px] text-zinc-400">{w.work_order_number}</span>
                          {w.is_recurring && <span className="inline-flex items-center gap-0.5 text-[8px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#8a6d1f]" title="Recurring Preventative Maintenance" data-testid={`service-card-pm-${w.id}`}><Repeat className="h-2.5 w-2.5" /> PM</span>}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/service/work-orders/${w.id}/pdf`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} draggable={false} data-testid={`service-card-pdf-${w.id}`} title="PDF" className="text-zinc-300 hover:text-[#D4AF37]"><Download className="h-3.5 w-3.5" /></a>
                          <button onClick={(e) => { e.stopPropagation(); setEmailing(w); }} onMouseDown={(e) => e.stopPropagation()} draggable={false} data-testid={`service-card-email-${w.id}`} title="Email" className="text-zinc-300 hover:text-[#D4AF37]"><Mail className="h-3.5 w-3.5" /></button>
                          <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${priorityBadge(w.priority)}`}>{w.priority}</span>
                        </div>
                      </div>
                      <div className="font-medium text-sm text-[#0F172A] truncate mt-0.5">{w.title || w.customer_name || "Work order"}</div>
                      <div className="text-xs text-zinc-500 truncate">{w.customer_name}</div>
                      <div className="flex items-center gap-1 text-[11px] text-zinc-400 mt-1">
                        <User className="h-3 w-3" /> {w.technician_name || "Unassigned"}
                      </div>
                      {w.scheduled_date && <div className="text-[11px] text-[#B8860B] font-mono mt-0.5">{(w.scheduled_date || "").slice(0, 10)}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {editing && <WorkOrderModal wo={editing === "new" ? null : editing} customers={customers} techs={techs} inventory={inventory} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onReload(); }} onReload={onReload} />}
      {emailing && <WoEmailModal wo={emailing} onClose={() => setEmailing(null)} />}
    </>
  );
}

/* ---------------- Work Orders list ---------------- */
function WorkOrdersTab({ workOrders, customers, techs, inventory, onReload, prefill, onPrefillConsumed }) {
  const [editing, setEditing] = useState(null); // wo | "new"
  const [prefillData, setPrefillData] = useState(null);
  const [emailing, setEmailing] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    if (prefill) { setPrefillData(prefill); setEditing("new"); onPrefillConsumed && onPrefillConsumed(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const remove = async (id) => {
    try { await api.delete(`/service/work-orders/${id}`); toast.success("Work order deleted"); onReload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed"); }
  };

  const filtered = workOrders.filter((w) =>
    [w.work_order_number, w.title, w.customer_name, w.technician_name].filter(Boolean).join(" ").toLowerCase().includes(search.toLowerCase())
  ).filter((w) => !statusFilter || w.status === statusFilter);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="service-wo-search" placeholder="Search WO#, customer, tech…" className="pl-9 pr-3 py-2 border border-zinc-300 text-sm w-64 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="service-wo-status-filter" className="px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
            <option value="">All statuses</option>
            {WO_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={() => setEditing("new")} data-testid="service-wo-new" className="flex items-center gap-2 bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors">
          <Plus className="h-4 w-4" /> New Work Order
        </button>
      </div>

      <div className="bg-white border border-zinc-200">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-400 text-sm" data-testid="service-wo-empty">No work orders yet. Click "New Work Order" to create one.</div>
        ) : (
          <div className="divide-y divide-zinc-100" data-testid="service-wo-list">
            {filtered.map((w) => (
              <div key={w.id} className="px-5 py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-zinc-400">{w.work_order_number}</span>
                    <span className="font-medium text-[#0F172A]">{w.title || "Work order"}</span>
                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${priorityBadge(w.priority)}`}>{w.priority}</span>
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border border-zinc-300 text-zinc-500">{w.status}</span>
                    {w.is_recurring && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#8a6d1f]" title="Recurring Preventative Maintenance" data-testid={`service-wo-pm-${w.id}`}><Repeat className="h-3 w-3" /> PM</span>}
                  </div>
                  <div className="text-sm text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                    <span>{w.customer_name}</span>
                    <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {w.technician_name || "Unassigned"}</span>
                    {w.scheduled_date && <span className="text-[#B8860B] font-mono">{(w.scheduled_date || "").slice(0, 10)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/service/work-orders/${w.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`service-wo-pdf-${w.id}`} title="PDF" className="text-zinc-400 hover:text-[#D4AF37] transition-colors"><Download className="h-4 w-4" /></a>
                  <button onClick={() => setEmailing(w)} data-testid={`service-wo-email-${w.id}`} title="Email" className="text-zinc-400 hover:text-[#D4AF37] transition-colors"><Mail className="h-4 w-4" /></button>
                  <button onClick={() => setEditing(w)} data-testid={`service-wo-edit-${w.id}`} className="text-zinc-400 hover:text-[#0F172A] transition-colors"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(w.id)} data-testid={`service-wo-del-${w.id}`} className="text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && <WorkOrderModal wo={editing === "new" ? null : editing} prefill={editing === "new" ? prefillData : null} customers={customers} techs={techs} inventory={inventory} onClose={() => { setEditing(null); setPrefillData(null); }} onSaved={() => { setEditing(null); setPrefillData(null); onReload(); }} onReload={onReload} />}
      {emailing && <WoEmailModal wo={emailing} onClose={() => setEmailing(null)} />}
    </div>
  );
}

/* ---------------- Work Order create/edit modal ---------------- */
function WorkOrderModal({ wo, prefill, customers, techs, inventory, onClose, onSaved, onReload }) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [form, setForm] = useState({
    customer_id: wo?.customer_id || prefill?.customer_id || "",
    technician_id: wo?.technician_id || "",
    title: wo?.title || prefill?.title || "",
    description: wo?.description || "",
    status: wo?.status || "Scheduled",
    priority: wo?.priority || "Medium",
    scheduled_date: (wo?.scheduled_date || "").slice(0, 10),
    service_address: wo?.service_address || "",
    notes: wo?.notes || "",
    checklist_template_id: wo?.checklist_template_id || "",
  });
  const [lines, setLines] = useState(wo?.items || []);
  const [picker, setPicker] = useState("");
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const isManager = ["admin", "manager", "sales_manager"].includes(user?.role);
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/service/checklist-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {}); }, []);
  const presetFromWo = () => {
    if (!wo?.is_recurring) return "Monthly";
    const u = wo.recurrence_unit, c = wo.recurrence_count;
    if (u === "month" && c === 1) return "Monthly";
    if (u === "month" && c === 3) return "Quarterly";
    if (u === "month" && c === 6) return "Semi-Annual";
    if (u === "month" && c === 12) return "Annual";
    return "Custom";
  };
  const [recurring, setRecurring] = useState(!!wo?.is_recurring);
  const [freq, setFreq] = useState(presetFromWo());
  const [customUnit, setCustomUnit] = useState(wo?.recurrence_unit || "month");
  const [customCount, setCustomCount] = useState(wo?.recurrence_count || 1);
  const [leadDays, setLeadDays] = useState(wo?.lead_days ?? 14);

  const addLine = () => {
    const it = inventory.find((i) => i.id === picker);
    if (!it) return;
    setLines((ls) => [...ls, { item_id: it.id, name: it.name, sku: it.sku, qty: 1, unit_price: it.unit_price }]);
    setPicker("");
  };
  const updLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const rmLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    setSaving(true);
    const freqMap = { Monthly: ["month", 1], Quarterly: ["month", 3], "Semi-Annual": ["month", 6], Annual: ["month", 12] };
    let rec = { is_recurring: false };
    if (isManager && recurring) {
      const [u, c] = freq === "Custom" ? [customUnit, parseInt(customCount, 10) || 1] : freqMap[freq];
      rec = { is_recurring: true, recurrence_unit: u, recurrence_count: c, lead_days: parseInt(leadDays, 10) || 0 };
    }
    const payload = {
      ...form,
      checklist_template_id: form.checklist_template_id || null,
      ...rec,
      scheduled_date: form.scheduled_date || null,
      technician_id: form.technician_id || null,
      items: lines.map((l) => ({ item_id: l.item_id || null, name: l.name, sku: l.sku || "", qty: parseFloat(l.qty) || 0, unit_price: parseFloat(l.unit_price) || 0 })),
    };
    try {
      if (wo?.id) await api.patch(`/service/work-orders/${wo.id}`, payload);
      else await api.post("/service/work-orders", payload);
      toast.success("Work order saved");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center overflow-y-auto py-10 px-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl" onClick={(e) => e.stopPropagation()} data-testid="service-wo-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-display font-bold text-[#0F172A]">{wo?.id ? `Edit ${wo.work_order_number}` : "New Work Order"}</h2>
          <button onClick={onClose} data-testid="service-wo-modal-close" className="text-zinc-400 hover:text-[#0F172A]"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Sel label="Customer" value={form.customer_id} onChange={(v) => setForm({ ...form, customer_id: v })} testid="service-wo-customer"
              options={[["", "Select customer…"], ...customers.map((c) => [c.id, c.company_name || c.email])]} />
            <Sel label="Technician" value={form.technician_id} onChange={(v) => setForm({ ...form, technician_id: v })} testid="service-wo-tech"
              options={[["", "Unassigned"], ...techs.map((t) => [t.id, t.name])]} />
          </div>
          <Inp label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="service-wo-title" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Sel label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} testid="service-wo-status" options={WO_STATUSES.map((s) => [s, s])} />
            <Sel label="Priority" value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} testid="service-wo-priority" options={WO_PRIORITIES.map((s) => [s, s])} />
            <Inp label="Scheduled Date" type="date" value={form.scheduled_date} onChange={(v) => setForm({ ...form, scheduled_date: v })} testid="service-wo-date" />
          </div>
          <Inp label="Service Address" value={form.service_address} onChange={(v) => setForm({ ...form, service_address: v })} testid="service-wo-address" placeholder="Defaults to the customer's address" />
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="service-wo-desc" rows={2} className="w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          </div>

          {!wo?.id && (
            <Sel label="Attach checklist (optional)" value={form.checklist_template_id} onChange={(v) => setForm({ ...form, checklist_template_id: v })} testid="service-wo-create-checklist"
              options={[["", "None"], ...templates.map((t) => [t.id, `${t.name}${t.type === "steps" ? "" : " (PDF)"}`])]} />
          )}

          {isManager && (
            <div className="border border-zinc-200 p-4" data-testid="service-wo-recurring">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} data-testid="service-wo-recurring-toggle" className="accent-[#D4AF37] h-4 w-4" />
                <span className="text-sm font-medium text-[#0F172A] inline-flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-[#D4AF37]" /> Recurring Preventative Maintenance</span>
              </label>
              {recurring && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Sel label="Frequency" value={freq} onChange={setFreq} testid="service-wo-recurring-freq" options={["Monthly", "Quarterly", "Semi-Annual", "Annual", "Custom"].map((f) => [f, f])} />
                  {freq === "Custom" && <Inp label="Every" type="number" value={customCount} onChange={setCustomCount} testid="service-wo-recurring-count" />}
                  {freq === "Custom" && <Sel label="Unit" value={customUnit} onChange={setCustomUnit} testid="service-wo-recurring-unit" options={[["week", "Weeks"], ["month", "Months"]]} />}
                  <Inp label="Appear on board (days before due)" type="number" value={leadDays} onChange={setLeadDays} testid="service-wo-recurring-lead" />
                </div>
              )}
              {recurring && <p className="mt-2 text-xs text-zinc-500">A new PM work order is auto-created on this cadence — when this one is completed, or {leadDays || 0} day(s) before it's next due — carrying over the technician and checklist.</p>}
            </div>
          )}

          {/* Parts from inventory */}
          <div className="border border-zinc-200">
            <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center justify-between gap-2 bg-zinc-50">
              <span className="text-sm font-medium text-[#0F172A]">Parts / Materials</span>
              {inventory.length > 0 ? (
                <div className="flex items-center gap-2">
                  <select value={picker} onChange={(e) => setPicker(e.target.value)} data-testid="service-wo-part-picker" className="px-2 py-1.5 border border-zinc-300 bg-white text-sm outline-none">
                    <option value="">Add part…</option>
                    {inventory.map((i) => <option key={i.id} value={i.id}>{i.name}{i.sku ? ` (${i.sku})` : ""}</option>)}
                  </select>
                  <button onClick={addLine} data-testid="service-wo-part-add" className="px-2.5 py-1.5 bg-[#0F172A] text-white text-sm hover:bg-[#1E293B]"><Plus className="h-4 w-4" /></button>
                </div>
              ) : <span className="text-xs text-zinc-400">Inventory not available</span>}
            </div>
            {lines.length === 0 ? (
              <div className="px-4 py-3 text-sm text-zinc-400">No parts added.</div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {lines.map((l, i) => (
                  <div key={i} className="px-4 py-2 flex items-center gap-3" data-testid={`service-wo-line-${i}`}>
                    <span className="flex-1 text-sm text-[#0F172A] truncate">{l.name}{l.sku ? <span className="text-zinc-400"> · {l.sku}</span> : null}</span>
                    <input type="number" value={l.qty} onChange={(e) => updLine(i, "qty", e.target.value)} data-testid={`service-wo-line-qty-${i}`} className="w-16 px-2 py-1 border border-zinc-300 text-sm" />
                    <input type="number" value={l.unit_price} onChange={(e) => updLine(i, "unit_price", e.target.value)} className="w-24 px-2 py-1 border border-zinc-300 text-sm" />
                    <button onClick={() => rmLine(i)} className="text-zinc-400 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {wo?.id && <WOPhotos wo={wo} onReload={onReload} />}

          {wo?.id && <WOChecklists wo={wo} />}

          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="service-wo-notes" rows={2} className="w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            {wo?.id && (
              <>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/service/work-orders/${wo.id}/pdf`} target="_blank" rel="noreferrer" data-testid="service-wo-modal-pdf" className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-zinc-600 border border-zinc-300 hover:bg-zinc-50"><Download className="h-4 w-4" /> PDF</a>
                <button onClick={() => setEmailOpen(true)} data-testid="service-wo-modal-email" className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-zinc-600 border border-zinc-300 hover:bg-zinc-50"><Mail className="h-4 w-4" /> Email</button>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2.5 text-sm text-zinc-600 hover:text-[#0F172A]">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="service-wo-save" className="flex items-center gap-2 bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#1E293B] disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save work order"}
            </button>
          </div>
        </div>
      </div>
      {emailOpen && <WoEmailModal wo={wo} onClose={() => setEmailOpen(false)} />}
    </div>
  );
}

/* ---------------- Work Order Checklists ---------------- */
function WOChecklists({ wo }) {
  const [templates, setTemplates] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [sel, setSel] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const [tRes, cRes] = await Promise.all([
        api.get("/service/checklist-templates"),
        api.get(`/service/work-orders/${wo.id}/checklists`),
      ]);
      setTemplates(tRes.data.templates || []);
      setChecklists(cRes.data.checklists || []);
    } catch (e) { toast.error("Could not load checklists"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [wo.id]);

  const add = async () => {
    if (!sel) { toast.error("Pick a checklist template"); return; }
    setAdding(true);
    try { await api.post(`/service/work-orders/${wo.id}/checklists`, { template_id: sel }); setSel(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add checklist"); }
    finally { setAdding(false); }
  };
  const remove = async (c) => {
    if (!window.confirm("Remove this checklist?")) return;
    try { await api.delete(`/service/checklists/${c.id}`); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="border border-zinc-200 p-4" data-testid="service-wo-checklists">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display font-bold text-[#0F172A]">Work Checklists</h3>
        <a href="/checklists" target="_blank" rel="noreferrer" data-testid="service-wo-manage-templates" className="text-sm text-[#7A1F2B] hover:underline">Manage Templates</a>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Required steps must be complete before review. Template edits never alter attached copies. Check steps and add notes, then Save Work Order.</p>
      <div className="flex items-stretch gap-2">
        <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="service-wo-checklist-picker" className="flex-1 px-3 py-2 border border-zinc-300 bg-zinc-50 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
          <option value="">Select a checklist template</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.type === "steps" ? "" : " (PDF)"}</option>)}
        </select>
        <button onClick={add} disabled={adding} data-testid="service-wo-checklist-add" className="px-4 bg-[#B97A82] text-white text-sm font-medium hover:bg-[#a86a72] disabled:opacity-60">{adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Attach Copy"}</button>
        <button type="button" onClick={load} data-testid="service-wo-checklist-refresh" className="px-4 bg-zinc-100 border border-zinc-300 text-sm hover:bg-zinc-200">Refresh</button>
      </div>
      {checklists.length === 0 ? (
        <div className="mt-4 text-sm text-zinc-400">{templates.length === 0 ? "No templates yet. Add them in Checklists." : "No checklist attached."}</div>
      ) : (
        <div className="mt-4 space-y-3">
          {checklists.map((c) => c.type === "steps"
            ? <StepChecklistCard key={c.id} cl={c} onRemove={() => remove(c)} />
            : <ChecklistCard key={c.id} cl={c} onChanged={load} onRemove={() => remove(c)} />)}
        </div>
      )}
    </div>
  );
}

function StepChecklistCard({ cl, onRemove }) {
  const [steps, setSteps] = useState(cl.steps || []);
  const total = steps.length;
  const done = steps.filter((s) => s.done).length;
  const reqRemaining = steps.filter((s) => s.required && !s.done).length;

  const persist = async (next) => {
    setSteps(next);
    try { await api.patch(`/service/checklists/${cl.id}`, { steps: next, status: reqRemainingOf(next) === 0 && next.length ? "Complete" : "Draft" }); }
    catch { toast.error("Save failed"); }
  };
  const reqRemainingOf = (arr) => arr.filter((s) => s.required && !s.done).length;
  const toggle = (i) => persist(steps.map((s, idx) => idx === i ? { ...s, done: !s.done } : s));
  const setNote = (i, v) => setSteps((xs) => xs.map((s, idx) => idx === i ? { ...s, notes: v } : s));

  return (
    <div className="border border-zinc-200 p-4" data-testid={`service-wo-checklist-${cl.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold text-[#0F172A]">{cl.template_name}</div>
          <div className="text-xs text-zinc-500 mt-0.5">Template v{cl.template_version || 1} · {done}/{total} steps complete · {reqRemaining} required remaining</div>
        </div>
        <button onClick={onRemove} data-testid={`service-wo-checklist-del-${cl.id}`} className="text-zinc-400 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="border-t border-zinc-200 mt-3 pt-3 space-y-4">
        {steps.length === 0 ? <div className="text-xs text-zinc-400">This checklist has no steps.</div> : steps.map((s, i) => (
          <div key={s.id || i}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!s.done} onChange={() => toggle(i)} data-testid={`service-wo-step-${cl.id}-${i}`} className="accent-[#7A1F2B] h-4 w-4" />
              <span className="text-sm text-[#0F172A]">{s.label}</span>
              {s.required && <span className="text-xs font-semibold text-[#7A1F2B]">Required</span>}
            </label>
            <textarea value={s.notes || ""} onChange={(e) => setNote(i, e.target.value)} onBlur={() => persist(steps)} placeholder="Step notes (optional)" rows={2} className="mt-2 w-full px-3 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ChecklistCard({ cl, onChanged, onRemove }) {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState(cl.values || {});
  const [saving, setSaving] = useState(false);
  const fillable = (cl.fields || []).filter((f) => f.type !== "signature");

  const setVal = (k, v) => setVals((x) => ({ ...x, [k]: v }));
  const save = async (status) => {
    setSaving(true);
    try { await api.patch(`/service/checklists/${cl.id}`, { values: vals, status: status || cl.status }); toast.success("Checklist saved"); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="px-4 py-3" data-testid={`service-wo-checklist-${cl.id}`}>
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left">
          <span className="text-sm font-medium text-[#0F172A]">{cl.template_name}</span>
          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 ${cl.status === "Complete" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{cl.status}</span>
          <span className="text-xs text-zinc-400">{open ? "▾" : "▸"}</span>
        </button>
        <div className="flex items-center gap-3 shrink-0">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/service/checklists/${cl.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`service-wo-checklist-pdf-${cl.id}`} title="Filled PDF" className="text-zinc-400 hover:text-[#D4AF37]"><Download className="h-4 w-4" /></a>
          <button onClick={onRemove} data-testid={`service-wo-checklist-del-${cl.id}`} className="text-zinc-400 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          {fillable.length === 0 ? <div className="text-xs text-zinc-400">No fillable fields detected in this PDF.</div> : fillable.map((f) => (
            <div key={f.name} className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-2 sm:items-center">
              <label className="text-xs text-zinc-600">{f.name}</label>
              {f.type === "checkbox" ? (
                <input type="checkbox" checked={!!vals[f.name] && vals[f.name] !== "Off"} onChange={(e) => setVal(f.name, e.target.checked)} data-testid={`checklist-field-${cl.id}`} className="accent-[#D4AF37] h-4 w-4" />
              ) : f.type === "choice" && (f.options || []).length ? (
                <select value={vals[f.name] || ""} onChange={(e) => setVal(f.name, e.target.value)} className="px-2 py-1.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
                  <option value="">—</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input value={vals[f.name] || ""} onChange={(e) => setVal(f.name, e.target.value)} className="px-2 py-1.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              )}
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <button onClick={() => save("Draft")} disabled={saving} className="px-3 py-1.5 text-sm border border-zinc-300 hover:bg-zinc-50 disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</button>
            <button onClick={() => save("Complete")} disabled={saving} data-testid={`service-wo-checklist-complete-${cl.id}`} className="px-3 py-1.5 text-sm bg-[#0F172A] text-white hover:bg-[#1E293B] disabled:opacity-60">Save &amp; mark complete</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Technicians ---------------- */
function TechniciansTab({ techs, onReload }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", skills: "", active: true });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => { setForm({ name: "", phone: "", email: "", skills: "", active: true }); setEditingId(null); };
  const openEdit = (t) => { setForm({ name: t.name || "", phone: t.phone || "", email: t.email || "", skills: t.skills || "", active: t.active !== false }); setEditingId(t.id); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editingId) await api.patch(`/service/technicians/${editingId}`, form);
      else await api.post("/service/technicians", form);
      toast.success("Technician saved"); reset(); onReload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/service/technicians/${id}`); toast.success("Technician removed"); if (editingId === id) reset(); onReload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={save} className="bg-white border border-zinc-200 p-6 space-y-4 lg:col-span-1 h-fit" data-testid="service-tech-form">
        <div className="flex items-center gap-2"><Users className="h-4 w-4 text-[#D4AF37]" /><h2 className="font-display font-bold text-[#0F172A]">{editingId ? "Edit Technician" : "Add Technician"}</h2></div>
        <Inp label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="service-tech-name" />
        <Inp label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="service-tech-phone" />
        <Inp label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="service-tech-email" />
        <Inp label="Skills / Notes" value={form.skills} onChange={(v) => setForm({ ...form, skills: v })} testid="service-tech-skills" />
        <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="service-tech-active" className="accent-[#D4AF37]" /> Active
        </label>
        <div className="flex gap-2">
          <button type="submit" disabled={saving} data-testid="service-tech-save" className="flex-1 flex items-center justify-center gap-2 bg-[#0F172A] text-white py-2.5 text-sm font-medium hover:bg-[#1E293B] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> {editingId ? "Save" : "Add technician"}</>}
          </button>
          {editingId && <button type="button" onClick={reset} className="px-4 py-2.5 text-sm text-zinc-600 border border-zinc-300 hover:bg-zinc-50">Cancel</button>}
        </div>
      </form>

      <div className="lg:col-span-2 bg-white border border-zinc-200">
        <div className="p-5 border-b border-zinc-200"><h2 className="font-display font-bold text-[#0F172A]">Technicians</h2></div>
        {techs.length === 0 ? (
          <div className="p-12 text-center text-zinc-400 text-sm" data-testid="service-tech-empty">No technicians yet.</div>
        ) : (
          <div className="divide-y divide-zinc-100" data-testid="service-tech-list">
            {techs.map((t) => (
              <div key={t.id} className="px-5 py-4 flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#0F172A]">{t.name}</span>
                    {t.active === false && <span className="text-[10px] uppercase tracking-wide text-zinc-400 border border-zinc-300 px-1.5 py-0.5">Inactive</span>}
                  </div>
                  <div className="text-sm text-zinc-500 flex items-center gap-3 flex-wrap mt-0.5">
                    {t.phone && <span>{t.phone}</span>}
                    {t.email && <span className="font-mono text-xs">{t.email}</span>}
                    {t.skills && <span className="text-zinc-400">{t.skills}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => openEdit(t)} data-testid={`service-tech-edit-${t.id}`} className="text-zinc-400 hover:text-[#0F172A]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(t.id)} data-testid={`service-tech-del-${t.id}`} className="text-zinc-400 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Schedule (day / week calendar) ---------------- */
function ScheduleTab({ workOrders, customers, techs, inventory, onReload }) {
  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [editing, setEditing] = useState(null);

  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
  const days = mode === "week"
    ? Array.from({ length: 7 }, (_, i) => { const s = startOfWeek(anchor); const x = new Date(s); x.setDate(s.getDate() + i); return x; })
    : [anchor];
  const woOn = (d) => workOrders.filter((w) => (w.scheduled_date || "").slice(0, 10) === fmt(d));
  const shift = (n) => { const x = new Date(anchor); x.setDate(x.getDate() + (mode === "week" ? n * 7 : n)); setAnchor(x); };
  const unscheduled = workOrders.filter((w) => !(w.scheduled_date || "").trim());

  const label = mode === "week"
    ? `Week of ${startOfWeek(anchor).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
    : anchor.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="service-sched-prev" className="px-3 py-2 border border-zinc-300 text-sm hover:bg-zinc-50">‹</button>
          <span className="text-sm font-medium text-[#0F172A] min-w-[170px] text-center" data-testid="service-sched-label">{label}</span>
          <button onClick={() => shift(1)} data-testid="service-sched-next" className="px-3 py-2 border border-zinc-300 text-sm hover:bg-zinc-50">›</button>
          <button onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setAnchor(d); }} data-testid="service-sched-today" className="px-3 py-2 border border-zinc-300 text-sm hover:bg-zinc-50">Today</button>
        </div>
        <div className="inline-flex border border-zinc-300">
          {[["day", "Day"], ["week", "Week"]].map(([m, l]) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`service-sched-${m}`} className={`px-4 py-2 text-sm ${mode === m ? "bg-[#0F172A] text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>{l}</button>
          ))}
        </div>
      </div>

      {techs.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2" data-testid="tech-workload">
          <span className="text-xs uppercase tracking-wide text-zinc-400 self-center mr-1">Open jobs:</span>
          {techs.map((t) => {
            const count = workOrders.filter((w) => w.technician_id === t.id && !["Completed", "Cancelled"].includes(w.status)).length;
            return (
              <span key={t.id} data-testid={`tech-workload-${t.id}`} className={`inline-flex items-center gap-1.5 text-xs border px-2.5 py-1 ${count === 0 ? "border-zinc-200 text-zinc-400" : count >= 5 ? "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444]" : "border-zinc-300 text-zinc-600"}`}>
                <User className="h-3 w-3" />{t.name}<span className="font-semibold">{count}</span>
              </span>
            );
          })}
          {(() => {
            const u = workOrders.filter((w) => !w.technician_id && !["Completed", "Cancelled"].includes(w.status)).length;
            return u > 0 ? <span data-testid="tech-workload-unassigned" className="inline-flex items-center gap-1.5 text-xs border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#B8860B] px-2.5 py-1"><User className="h-3 w-3" />Unassigned<span className="font-semibold">{u}</span></span> : null;
          })()}
        </div>
      )}

      <div className={`grid gap-2 ${mode === "week" ? "grid-cols-2 md:grid-cols-7" : "grid-cols-1"}`} data-testid="service-schedule">
        {days.map((d) => {
          const list = woOn(d);
          const isToday = fmt(d) === fmt(new Date());
          return (
            <div key={fmt(d)} className={`bg-zinc-50 border border-zinc-200 p-2 min-h-[140px] ${isToday ? "border-t-2 border-t-[#D4AF37]" : ""}`} data-testid={`service-sched-day-${fmt(d)}`}>
              <div className="px-1 pb-2">
                <div className="text-xs font-semibold text-[#0F172A]">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                <div className="text-[11px] text-zinc-400">{d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
              </div>
              <div className="space-y-2">
                {list.length === 0 ? <span className="text-[11px] text-zinc-300 px-1">—</span> :
                  list.map((w) => (
                    <button key={w.id} onClick={() => setEditing(w)} data-testid={`service-sched-card-${w.id}`} className="w-full text-left border border-zinc-200 bg-white p-2 hover:shadow-sm transition-shadow">
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-mono text-[10px] text-zinc-400">{w.work_order_number}</span>
                        <span className={`text-[9px] uppercase px-1 py-0.5 rounded-full ${priorityBadge(w.priority)}`}>{w.priority}</span>
                      </div>
                      <div className="text-xs font-medium text-[#0F172A] truncate mt-0.5">{w.title || w.customer_name}</div>
                      <div className="text-[11px] text-zinc-500 flex items-center gap-1"><User className="h-3 w-3" />{w.technician_name || "Unassigned"}</div>
                    </button>
                  ))}
              </div>
            </div>
          );
        })}
      </div>

      {unscheduled.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">Unscheduled ({unscheduled.length})</h3>
          <div className="flex flex-wrap gap-2" data-testid="service-sched-unscheduled">
            {unscheduled.map((w) => (
              <button key={w.id} onClick={() => setEditing(w)} data-testid={`service-sched-unsched-${w.id}`} className="border border-zinc-200 bg-white p-2 text-left hover:shadow-sm transition-shadow">
                <span className="font-mono text-[10px] text-zinc-400">{w.work_order_number}</span>
                <div className="text-xs font-medium text-[#0F172A] truncate max-w-[160px]">{w.title || w.customer_name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {editing && <WorkOrderModal wo={editing} customers={customers} techs={techs} inventory={inventory} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onReload(); }} onReload={onReload} />}
    </div>
  );
}

/* ---------------- small field helpers ---------------- */
function Inp({ label, value, onChange, testid, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
    </div>
  );
}
function Sel({ label, value, onChange, testid, options }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

/* ---------------- Email modal ---------------- */
function WoEmailModal({ wo, onClose }) {
  const [to, setTo] = useState(wo.emailed_to || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/service/work-orders/${wo.id}/email`, { to: to || null, message });
      toast.success(`Emailed to ${data.to}`);
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed"); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-start justify-center py-16 px-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="service-wo-email-modal">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200">
          <h3 className="font-display font-bold text-[#0F172A]">Email {wo.work_order_number}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-[#0F172A]"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Inp label="Recipient email" value={to} onChange={setTo} testid="service-wo-email-to" placeholder="Defaults to the customer's email" />
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Message (optional)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="service-wo-email-msg" rows={3} className="w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          </div>
        </div>
        <div className="px-5 py-3.5 border-t border-zinc-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-600 hover:text-[#0F172A]">Cancel</button>
          <button onClick={send} disabled={sending} data-testid="service-wo-email-send" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2 text-sm font-medium hover:bg-[#1E293B] disabled:opacity-60">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4" /> Send</>}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Job photos ---------------- */
function WOImage({ wid, pid, onEnlarge }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url; let active = true;
    api.get(`/service/work-orders/${wid}/photos/${pid}`, { responseType: "blob" })
      .then(({ data }) => { if (active) { url = URL.createObjectURL(data); setSrc(url); } }).catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [wid, pid]);
  return (
    <button type="button" onClick={() => src && onEnlarge && onEnlarge(src)} className="h-20 w-20 shrink-0 bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center cursor-zoom-in">
      {src ? <img src={src} alt="job" className="h-full w-full object-cover" /> : <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
    </button>
  );
}

function WOPhotos({ wo, onReload }) {
  const [photos, setPhotos] = useState(wo.photos || []);
  const [uploading, setUploading] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => { setPhotos(wo.photos || []); }, [wo]);

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("kind", kind); fd.append("file", file);
      const { data } = await api.post(`/service/work-orders/${wo.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.photo]);
      toast.success(`${kind === "before" ? "Before" : "After"} photo added`);
      onReload && onReload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(null); }
  };
  const del = async (pid) => {
    const prev = photos;
    setPhotos((p) => p.filter((x) => x.id !== pid));
    try { await api.delete(`/service/work-orders/${wo.id}/photos/${pid}`); onReload && onReload(); }
    catch { setPhotos(prev); toast.error("Delete failed"); }
  };
  const group = (k) => photos.filter((p) => p.kind === k);

  const Section = ({ kind, label }) => (
    <div className="flex-1 min-w-[160px]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
        <label className="flex items-center gap-1 cursor-pointer text-xs text-[#0F172A] hover:text-[#D4AF37]" data-testid={`service-wo-photo-add-${kind}`}>
          {uploading === kind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Add
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(kind, e.target.files?.[0])} />
        </label>
      </div>
      <div className="flex flex-wrap gap-2" data-testid={`service-wo-photos-${kind}`}>
        {group(kind).length === 0 ? <span className="text-xs text-zinc-400">No {label.toLowerCase()} photos.</span> :
          group(kind).map((p) => (
            <div key={p.id} className="relative">
              <WOImage wid={wo.id} pid={p.id} onEnlarge={setLightbox} />
              <button onClick={() => del(p.id)} data-testid={`service-wo-photo-del-${p.id}`} className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-300 rounded-full p-0.5 text-zinc-400 hover:text-[#EF4444]"><X className="h-3 w-3" /></button>
            </div>
          ))}
      </div>
    </div>
  );

  return (
    <div className="border border-zinc-200 p-4" data-testid="service-wo-photos">
      <div className="flex items-center gap-2 mb-3"><Camera className="h-4 w-4 text-[#D4AF37]" /><span className="text-sm font-medium text-[#0F172A]">Job Photos</span></div>
      <div className="flex gap-6 flex-wrap">
        <Section kind="before" label="Before" />
        <Section kind="after" label="After" />
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4 cursor-zoom-out" onClick={() => setLightbox(null)} data-testid="service-wo-photo-lightbox">
          <img src={lightbox} alt="job" className="max-h-[90vh] max-w-[90vw] object-contain" />
        </div>
      )}
    </div>
  );
}
